import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';

const HomePage = () => {

  const [selectedAgents, setSelectedAgents] = useState([]);
  const [customAgent1, setCustomAgent1] = useState('');
  const [customAgent2, setCustomAgent2] = useState('');
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract user_id from URL parameters and persist to localStorage; fallback to stored value
  useEffect(() => {
    const paramUserId = searchParams.get('user_id')
      || searchParams.get('userid')
      || searchParams.get('client_reference_id');

    if (paramUserId && paramUserId !== userId) {
      setUserId(paramUserId);
      try { localStorage.setItem('user_id', paramUserId); } catch (_) {}
    } else if (!userId) {
      try {
        const stored = localStorage.getItem('user_id');
        if (stored) setUserId(stored);
      } catch (_) {}
    }
  }, [searchParams, userId]);

  // 20 AI agents available for selection
  const availableAgents = [
    { id: 1, name: 'AI Phone Receptionist Agent', icon: '📞' },
    { id: 2, name: 'AI Lead Qualifier Chatbot (image and video processing)', icon: '🤖' },
    { id: 3, name: 'AI Booking Agent', icon: '📅' },
    { id: 4, name: 'AI Follow Up Agent', icon: '🔄' },
    { id: 5, name: 'AI Review Collector', icon: '⭐' },
    { id: 6, name: 'AI Cold Outreach Agent', icon: '❄️' },
    { id: 7, name: 'AI Cold Lead Generator Agent', icon: '🎯' },
    { id: 8, name: 'AI Lead Nurture Agent', icon: '🌱' },
    { id: 9, name: 'AI Affiliate Recruitment Agent', icon: '🤝' },
    { id: 10, name: 'AI Quote Generator Agent', icon: '💰' },
    { id: 11, name: 'AI DIY Lead Generator Agent', icon: '🛠️' },
    { id: 12, name: 'AI "Before We Arrive" Prep Agent', icon: '📋' },
    { id: 13, name: 'AI Utility Savings Calculator Agent', icon: '🧮' },
    { id: 14, name: 'AI Meeting Notes Keeper', icon: '📝' },
    { id: 15, name: 'AI Maintenance Reminder Agent', icon: '🔔' },
    { id: 16, name: 'AI Emergency Responder and Dispatcher', icon: '🚨' },
    { id: 17, name: 'AI Social Media Manager Agent', icon: '📱' },
    { id: 18, name: 'AI Recruitment Agent', icon: '👥' },
    { id: 19, name: 'AI Web Receptionist (can be equipped with a slide)', icon: '🌐' },
    { id: 20, name: 'AI HVAC & Trades Service Optimizer Agent', icon: '🔧' }
  ];

  const toggleAgent = (agentId) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  // Calculate total agents (selected + custom)
  const totalAgents = selectedAgents.length + (customAgent1.trim() ? 1 : 0) + (customAgent2.trim() ? 1 : 0);
  const canActivate = totalAgents >= 5;

  const handleStartActivate = () => {
    if (!canActivate) {
      alert('Please select at least 5 agents total (you can select 5 regular agents, or 3 regular agents + 2 custom agents).');
      return;
    }
    
    // Navigate to presentation page with selected agents and user_id
    const selectedAgentNames = selectedAgents.map(id => 
      availableAgents.find(agent => agent.id === id)?.name
    ).filter(Boolean);
    
    // Add custom agents if provided
    if (customAgent1.trim()) selectedAgentNames.push(`Custom Agent 1: ${customAgent1.trim()}`);
    if (customAgent2.trim()) selectedAgentNames.push(`Custom Agent 2: ${customAgent2.trim()}`);
    
    // Persist to localStorage for fallback on destination page
    try {
      localStorage.setItem('selected_agents', JSON.stringify(selectedAgentNames));
      if (userId) localStorage.setItem('user_id', userId);
    } catch (_) {}
    
    navigate('/sales-pitch-onsite-call-from-ai', {
      state: {
        selectedAgents: selectedAgentNames,
        userId: userId
      }
    });
  };

  return (
    <div className="home-page">
      <style>{`
        @keyframes colorBlink {
          0% { color: #F8B700; } /* Yellow */
          33% { color: #4EAF47; } /* Green */
          66% { color: #ffffff; } /* White */
          100% { color: #F8B700; } /* Back to Yellow */
        }
        
        @media (max-width: 768px) {
          .agent-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          
          .agent-card {
            padding: 16px !important;
          }
          
          .agent-card h3 {
            font-size: 0.9rem !important;
          }
          
          .hero-title {
            font-size: 2rem !important;
          }
          
          .hero-subtitle {
            font-size: 1.2rem !important;
          }
          
          .features-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
        }
      `}</style>
      <Header />
      

      {/* Hero Section with Features */}
      <section style={{
        background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--accent-color) 100%)',
        color: 'white',
        padding: '60px 0 80px 0',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
                      <h1 className="hero-title" style={{ 
              fontSize: '3rem', 
              fontWeight: '700', 
              marginTop: '40px',
              marginBottom: '20px',
              color: 'white'
            }}>
            Activate Your AI Business Revolution
          </h1>
          <p className="hero-subtitle" style={{ 
            fontSize: '1.5rem', 
            marginBottom: '60px',
            color: 'white',
            opacity: 0.95
          }}>
            Get 19 AI Agents + 2 Custom Agents + $2,499 Guarantee
          </p>
          
          <h2 style={{ 
            textAlign: 'center', 
            fontSize: '2.5rem', 
            fontWeight: '700', 
            color: 'white',
            marginBottom: '60px',
            animation: 'colorBlink 3s infinite'
          }}>
            What You Get When You Activate
          </h2>
          
          <div className="features-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '30px',
            marginBottom: '60px'
          }}>
            {/* Core Package */}
                        <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              border: '2px solid var(--primary-color)',
              textAlign: 'left'
            }}>
              <h3 style={{ color: 'var(--primary-color)', fontSize: '1.5rem', marginBottom: '20px', fontWeight: '800' }}>
                Core Package
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--primary-color)', fontWeight: 'bold' }}>✓</span>
                  Win $2,499 in 30 days when AI gets you 5 extra jobs
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--primary-color)', fontWeight: 'bold' }}>✓</span>
                  19 AI agents + 2 Requested AI agents ($60,000 value)
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--primary-color)', fontWeight: 'bold' }}>✓</span>
                  125,000,000 AI credits for OpenAI ($1,000 value)
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--primary-color)', fontWeight: 'bold' }}>✓</span>
                  Setup & Customization by Dedicated Tech Support ($10,000 value)
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--primary-color)', fontWeight: 'bold' }}>✓</span>
                  Access to 500 human-like AI voices
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--primary-color)', fontWeight: 'bold' }}>✓</span>
                  48 hours ETA
                </li>
              </ul>
          </div>


                        {/* Guarantee & Extras */}
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              border: '2px solid var(--secondary-color)',
              textAlign: 'left'
            }}>
              <h3 style={{ color: 'var(--secondary-color)', fontSize: '1.5rem', marginBottom: '20px', fontWeight: '800' }}>
                Guarantee & Extras
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--secondary-color)', fontWeight: 'bold' }}>✓</span>
                  30 Days Business Boost Guarantee
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--secondary-color)', fontWeight: 'bold' }}>✓</span>
                  Extra 125,000,000 AI credits if not satisfied
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--secondary-color)', fontWeight: 'bold' }}>✓</span>
                  Access to other AI LLMs (Gemini, Anthropic, DeepSeek, XGroq)
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--secondary-color)', fontWeight: 'bold' }}>✓</span>
                  Access to all Agents' Analytics
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--secondary-color)', fontWeight: 'bold' }}>✓</span>
                  Share agents with employees, friends, or colleagues
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--secondary-color)', fontWeight: 'bold' }}>✓</span>
                  Integrate any third-party app
                </li>
              </ul>
        </div>


                        {/* Mobile & Database */}
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              border: '2px solid var(--accent-color)',
              textAlign: 'left'
            }}>
              <h3 style={{ color: 'var(--accent-color)', fontSize: '1.5rem', marginBottom: '20px', fontWeight: '800' }}>
                Mobile & Database
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--accent-color)', fontWeight: 'bold' }}>✓</span>
                  AI app for your business
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--accent-color)', fontWeight: 'bold' }}>✓</span>
                  Access to launch the mobile app
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--accent-color)', fontWeight: 'bold' }}>✓</span>
                  100,000 cold leads database in your area
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: '600' }}>
                  <span style={{ marginRight: '8px', color: 'var(--accent-color)', fontWeight: 'bold' }}>✓</span>
                  Each agent can be customized for you
                </li>
              </ul>
                </div>
              </div>

          
          {/* Scroll to Agent Selection Button */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => {
                document.getElementById('agent-selection').scrollIntoView({ 
                  behavior: 'smooth' 
                });
              }}
              style={{
                backgroundColor: 'white',
                color: 'var(--primary-color)',
                border: '2px solid white',
                borderRadius: '12px',
                padding: '16px 32px',
                fontSize: '1.25rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--primary-color)';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = 'var(--primary-color)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 14px 0 rgba(0, 0, 0, 0.2)';
              }}
            >
              Select Your AI Agents
            </button>
          </div>
        </div>
      </section>


      {/* Agent Selection Section */}
      <section id="agent-selection" style={{ padding: '80px 0', backgroundColor: 'white' }}>

        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ 
              fontSize: '2.5rem', 
              fontWeight: '700', 
              color: '#1f2937',
              marginBottom: '20px'
            }}>
              Select Your AI Agents
          </h2>

            <p style={{ 
              fontSize: '1.25rem', 
              color: '#6b7280',
              marginBottom: '20px'
            }}>
              Choose at least 5 agents total (you can select 5 regular agents, or 3 regular + 2 custom)
            </p>
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '16px',
              display: 'inline-block'
            }}>
              <p style={{ margin: 0, color: '#92400e', fontWeight: '600' }}>
                Current Selection: {totalAgents}/5 agents
              </p>
          </div>
        </div>


          {/* Agent Selection Grid */}
          <div className="agent-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '40px'
          }}>
            {availableAgents.map((agent) => (
              <div
                key={agent.id}
                className="agent-card"
                onClick={() => toggleAgent(agent.id)}
                style={{
                  padding: '20px',
                  border: selectedAgents.includes(agent.id) 
                    ? '2px solid var(--primary-color)' 
                    : '2px solid var(--border-color)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: selectedAgents.includes(agent.id) 
                    ? 'var(--green-light)' 
                    : 'var(--background-white)',
                  textAlign: 'center',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!selectedAgents.includes(agent.id)) {
                    e.target.style.borderColor = 'var(--primary-color)';
                    e.target.style.backgroundColor = 'var(--background-light)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedAgents.includes(agent.id)) {
                    e.target.style.borderColor = 'var(--border-color)';
                    e.target.style.backgroundColor = 'var(--background-white)';
                  }
                }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '12px' }}>
                  {agent.icon}
                </div>

                <h3 style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600', 
                  color: 'var(--text-primary)',
                  margin: 0
                }}>
                  {agent.name}
                </h3>
                {selectedAgents.includes(agent.id) && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'var(--primary-color)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    ✓
                  </div>
                )}
              </div>
            ))}
          </div>


          {/* Custom Agent Inputs */}
          <div style={{
            backgroundColor: '#f8fafc',
            padding: '30px',
            borderRadius: '16px',
            marginBottom: '40px'
          }}>
            <h3 style={{ 
              textAlign: 'center', 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: '#1f2937',
              marginBottom: '20px'
            }}>
              Request Custom AI Agents (Optional)
            </h3>
            <p style={{ 
              textAlign: 'center', 
              color: '#6b7280',
              marginBottom: '30px'
            }}>
              Describe 2 custom AI agents you'd love to have for your business
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontWeight: '600', 
                  color: '#1f2937',
                  marginBottom: '8px'
                }}>
                  Custom Agent 1:
                </label>
                <textarea
                  value={customAgent1}
                  onChange={(e) => setCustomAgent1(e.target.value)}
                  placeholder="Describe your first custom AI agent..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  fontWeight: '600', 
                  color: '#1f2937',
                  marginBottom: '8px'
                }}>
                  Custom Agent 2:
                </label>
                <textarea
                  value={customAgent2}
                  onChange={(e) => setCustomAgent2(e.target.value)}
                  placeholder="Describe your second custom AI agent..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Selection Summary */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '40px',
            padding: '20px',
            backgroundColor: canActivate ? '#f0fdf4' : '#fef2f2',
            borderRadius: '12px',
            border: `2px solid ${canActivate ? '#16a34a' : '#ef4444'}`
          }}>
            <p style={{ 
              fontSize: '1.2rem', 
              color: canActivate ? '#16a34a' : '#ef4444',
              margin: 0,
              fontWeight: '600'
            }}>
              {canActivate ? '✅ Ready to Activate!' : `❌ Need ${5 - totalAgents} more agent${5 - totalAgents !== 1 ? 's' : ''}`}
            </p>
            <p style={{ 
              fontSize: '1rem', 
              color: '#6b7280',
              margin: '8px 0 0 0'
            }}>
              {selectedAgents.length} regular agents + {[customAgent1.trim(), customAgent2.trim()].filter(Boolean).length} custom agents = {totalAgents} total
            </p>
          </div>

          {/* Start Activate Button */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={handleStartActivate}
              disabled={!canActivate}
              style={{
                backgroundColor: canActivate ? 'var(--primary-color)' : 'var(--text-light)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '20px 60px',
                fontSize: '1.5rem',
                fontWeight: '700',
                cursor: canActivate ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                minWidth: '300px',
                boxShadow: canActivate ? '0 4px 14px 0 rgba(78, 175, 71, 0.4)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (canActivate) {
                  e.target.style.backgroundColor = '#3d8b37';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(78, 175, 71, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (canActivate) {
                  e.target.style.backgroundColor = 'var(--primary-color)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 14px 0 rgba(78, 175, 71, 0.4)';
                }
              }}
            >
              🚀 Start Activate
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

